import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import cx from 'classnames';
import { Button, Card, CardContent, Checkbox, CircularProgress, Typography, } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

import styles from './NbaPage.module.css';

import {
  actionKey,
  addIds,
  buildActionByKey,
  formatActionConditions,
  formMilestoneKey,
  getDisplayContextLabel,
  getMilestoneNameFromMilestoneKey,
  isMilestoneKey
} from './nba.utils';
import { duplicateSelectedActions, duplicateSelectedMilestones } from './nba.service';
import { Action, FunctionDefinition, Milestone, RenderableComponent, Step } from './types';
import { EditActionDialog } from './EditActionDialog';
import { EditMilestoneDialog } from './EditMilestoneDialog';
import { EditStepDialog } from './EditStepDialog';
import { CustomAccordion } from './CustomAccordion';
import { DuplicateActionsDialog } from './DuplicateActionsDialog';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { NbaToolbar } from './NbaToolbar';
import { localStorageKeys } from '../constants';
import { MilestoneIcon } from './milestoneIcons';
import {
  createAction,
  createMilestone,
  createStep,
  deleteActionByName,
  deleteMilestoneByName,
  deleteStepByName,
  fetchComponents,
  fetchFunctions,
  fetchNbaGuide,
  updateAction,
  updateMilestone,
  updateStep,
} from './api';

export const NbaPage = () => {
  const notify = useNotify();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [availableFunctions, setAvailableFunctions] = useState<FunctionDefinition[]>([]);
  const [availableComponents, setAvailableComponents] = useState<RenderableComponent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [isMilestoneDialogOpen, setIsMilestoneDialogOpen] = useState(false);

  const [editingStep, setEditingStep] = useState<{ step: Step | null, milestoneName: string }>({
    step: null,
    milestoneName: ''
  });
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);

  const [editingAction, setEditingAction] = useState<{
    action: Action | null,
    milestoneName: string,
    stepName: string
  }>({ action: null, milestoneName: '', stepName: '' });
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

  const [isDuplicateActionsDialogOpen, setIsDuplicateActionsDialogOpen] = useState(false);

  const actionByKey = useMemo(() => {
    return buildActionByKey(milestones);
  }, [milestones]);

  const allActionIds = useMemo(() => Array.from(actionByKey.keys()), [actionByKey]);
  const allMilestoneIds = useMemo(() => milestones.map(({ name }) => formMilestoneKey(name)), [milestones]);
  const allSelectableIds = useMemo(() => [...allMilestoneIds, ...allActionIds], [allMilestoneIds, allActionIds]);

  const {
    selectedIds,
    clearSelection,
    enabled,
    setSelected,
    reset,
    setEnabled
  } = useBulkSelection(allSelectableIds);

  const selectedMilestoneNames = useMemo(
    () => Array.from(selectedIds).filter(isMilestoneKey).map(getMilestoneNameFromMilestoneKey),
    [selectedIds]
  );

  const selectedActionKeys = useMemo(
    () => Array.from(selectedIds).filter((id) => !isMilestoneKey(id)),
    [selectedIds]
  );

  const loadData = useCallback(async ({ initial }: { initial: boolean }) => {
    if (initial) {
      setLoading(true);
      setError(null);
    } else {
      setRefreshing(true);
    }
    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const [rawMilestones, functions, components] = await Promise.all([
        fetchNbaGuide(authToken),
        fetchFunctions(authToken),
        fetchComponents(authToken)
      ]);

      setMilestones(addIds(rawMilestones));
      setAvailableFunctions(functions);
      setAvailableComponents(components);
      clearSelection();

    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unknown error occurred while fetching data.';

      if (initial) {
        setError(message);
      } else {
        notify(message, 'warning');
      }
    } finally {
      if (initial) {
        setLoading(false);
      }

      setRefreshing(false);
    }
  }, [clearSelection, notify]);

  useEffect(() => {
    loadData({ initial: true });
  }, [loadData]);

  const handleSaveMilestone = async (milestoneData: Milestone) => {
    const isNew = !editingMilestone;
    const milestoneName = milestoneData.name;
    if (!milestoneName) {
      notify('Milestone name cannot be empty.', 'warning');
      return;
    }

    try {
      if (isNew) {
        await createMilestone(milestoneData);
      } else {
        await updateMilestone(editingMilestone.name, milestoneData);
      }

      await loadData({ initial: false });
      notify(`Milestone "${milestoneName}" saved successfully.`, 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Save failed.', 'warning');
    } finally {
      setIsMilestoneDialogOpen(false);
    }
  };

  const handleDuplicateSelectedMilestones = async (names: string[]) => {
    if (names.length === 0) {
      return;
    }

    if (refreshing) {
      return;
    }

    setRefreshing(true);
    try {
      const nextMilestones = await duplicateSelectedMilestones(names, milestones);
      await loadData({ initial: false });
      notify(`Duplicated ${nextMilestones.length + 1} milestone(s).`, 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Duplication failed.', 'warning');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDuplicateSelected = async () => {
    if (selectedIds.size === 0) {
      return;
    }

    if (refreshing) {
      return;
    }

    const hasMilestones = selectedMilestoneNames.length > 0;
    const hasActions = selectedActionKeys.length > 0;

    if (hasMilestones && hasActions) {
      notify('Select either milestones or actions (not both).', 'warning');
      return;
    }

    if (hasMilestones) {
      await handleDuplicateSelectedMilestones(selectedMilestoneNames);
      return;
    }

    setIsDuplicateActionsDialogOpen(true);
  };

  const handleConfirmDuplicateActions = async (targetMilestoneName: string, targetStepName: string) => {
    if (selectedIds.size === 0) {
      return;
    }

    if (refreshing) {
      return;
    }

    setRefreshing(true);
    try {
      const nextActions = await duplicateSelectedActions({
        targetMilestoneName,
        targetStepName,
        selectedIds,
        milestones,
      });

      await loadData({ initial: false });

      notify(`Duplicated ${nextActions.length + 1} action(s).`, 'info');
      setIsDuplicateActionsDialogOpen(false);
      clearSelection();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Duplication failed.', 'warning');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteMilestone = async (milestoneName: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete the "${milestoneName}" milestone?`)) return;
    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      await deleteMilestoneByName(milestoneName);
      setMilestones(prev => prev.filter(m => m.name !== milestoneName));
      notify('Milestone deleted successfully.', 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Deletion failed.', 'warning');
    }
  };

  const handleSaveStep = async (stepData: Step) => {
    const { milestoneName, step } = editingStep;
    const isNew = !step;
    if (!stepData.name) {
      notify('Step name cannot be empty.', 'warning');
      return;
    }

    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      if (isNew) {
        await createStep(milestoneName, stepData);
      } else {
        await updateStep(milestoneName, step.name, stepData);
      }

      await loadData({ initial: false });
      notify('Step saved successfully.', 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Save failed.', 'warning');
    } finally {
      setIsStepDialogOpen(false);
    }
  };

  const handleDeleteStep = async (milestoneName: string, stepName: string) => {
    if (!window.confirm(`Are you sure you want to delete the step "${stepName}"?`)) return;
    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      await deleteStepByName(milestoneName, stepName);
      await loadData({ initial: false });
      notify('Step deleted successfully.', 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Deletion failed.', 'warning');
    }
  };

  const handleSaveAction = async (actionData: Action) => {
    const { milestoneName, stepName, action } = editingAction;
    const isNew = !action;
    if (!actionData.name) {
      notify('Action name cannot be empty.', 'warning');
      return;
    }

    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      if (isNew) {
        await createAction(milestoneName, stepName, actionData);
      } else {
        await updateAction(milestoneName, stepName, action.name, actionData);
      }

      await loadData({ initial: false });
      notify('Action saved successfully.', 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Save failed.', 'warning');
    } finally {
      setIsActionDialogOpen(false);
    }
  };

  const handleDeleteAction = async (milestoneName: string, stepName: string, actionName: string) => {
    if (!window.confirm(`Are you sure you want to delete the action "${actionName}"?`)) return;
    try {
      const authToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      await deleteActionByName(milestoneName, stepName, actionName);
      await loadData({ initial: false });
      notify('Action deleted successfully.', 'info');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Deletion failed.', 'warning');
    }
  };

  const renderHeader = ({
    title,
    onEdit,
    onDelete,
    iconId,
    extraButtons,
    titlePrefix,
    selected,
  }: {
    title: string;
    onEdit: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onDelete: (event: React.MouseEvent<HTMLButtonElement>) => void;
    iconId?: string;
    extraButtons?: React.ReactNode;
    titlePrefix?: React.ReactNode;
    selected?: boolean;
  }) => (
    <div className={cx(styles.accordionHeader, { [styles.accordionHeaderSelected]: selected })}>
      <span className={styles.accordionTitle}>
        {titlePrefix}
        <MilestoneIcon iconId={iconId}/>
        {title}
      </span>
      <div>
        {extraButtons}
        <button
          className={styles.iconButton}
          onClick={onEdit}
        >
          <EditIcon fontSize="small"/>
        </button>
        <button
          className={styles.iconButton}
          onClick={onDelete}
        >
          <DeleteIcon fontSize="small"/>
        </button>
      </div>
    </div>
  );

  const renderAction = (milestone: Milestone, step: Step, action: Action) => {
    const key = actionKey(milestone.name, step.name, action.name);
    const isSelected = enabled && selectedIds.has(key);
    return (
      <div
        className={cx(styles.actionCard, {
          [styles.actionCardSelected]: isSelected,
          [styles.actionCardWithCheckbox]: enabled,
        })}
        key={key}
      >
        {enabled && (
          <div className={styles.actionCheckbox}>
            <Checkbox
              checked={isSelected}
              color="primary"
              onChange={(e) =>
                setSelected(
                  [key],
                  e.target.checked
                )
              }
            />
          </div>
        )}
        <div className={styles.actionButtons}>
          <button
            className={styles.iconButton}
            onClick={() => {
              setEditingAction({ action, milestoneName: milestone.name, stepName: step.name });
              setIsActionDialogOpen(true);
            }}
          >
            <EditIcon fontSize="small"/>
          </button>
          <button
            className={styles.iconButton}
            onClick={() => handleDeleteAction(milestone.name, step.name, action.name)}
          >
            <DeleteIcon fontSize="small"/>
          </button>
        </div>
        <h4 className={styles.actionHeader}>
          {action.name}
          {' '}
          (Order:
          {' '}
          {action.order}
          )
        </h4>
        <p className={styles.actionDescription}>
          {action.description}
        </p>
        <hr/>
        <div className={styles.actionDetails}>
          <p>
            <strong>Identifier:</strong>
            {' '}
            {action.onActionCallIdentifier}
          </p>
          <p>
            <strong>Component:</strong>
            {' '}
            {action.renderAnotherComponent || 'None'}
          </p>
          <p>
            <strong>Display Context:</strong>
            {' '}
            {getDisplayContextLabel(action.displayContext)}
          </p>
          <p>
            <strong>Conditions:</strong>
            {' '}
            {formatActionConditions(action.conditions)}
          </p>
        </div>
      </div>
    );
  };

  const renderStep = (milestone: Milestone, step: Step) => (
    <CustomAccordion
      className={styles.stepAccordion}
      key={`step||${milestone.name}||${step.name}`}
      title={renderHeader({
        title: `${step.name} (Order: ${step.order ?? 0})`,
        onEdit: (e) => {
          e.stopPropagation();
          setEditingStep({ step, milestoneName: milestone.name });
          setIsStepDialogOpen(true);
        },
        onDelete: (e) => {
          e.stopPropagation();
          handleDeleteStep(milestone.name, step.name);
        },
      })}
    >
      {step.actions
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((action) => renderAction(milestone, step, action))}
      <Button
        startIcon={<AddIcon/>}
        onClick={() => {
          setEditingAction({ action: null, milestoneName: milestone.name, stepName: step.name });
          setIsActionDialogOpen(true);
        }}
      >
        Add Action
      </Button>
    </CustomAccordion>
  );

  const renderMilestone = (milestone: Milestone) => {
    const key = formMilestoneKey(milestone.name);
    const isSelected = enabled && selectedIds.has(key);
    return (
      <CustomAccordion
        key={key}
        title={renderHeader({
          title: `${milestone.name} (Order: ${(milestone as any).order ?? 0})`,
          onEdit: (e) => {
            e.stopPropagation();
            setEditingMilestone(milestone);
            setIsMilestoneDialogOpen(true);
          },
          onDelete: (e) => {
            e.stopPropagation();
            handleDeleteMilestone(milestone.name);
          },
          iconId: (milestone as any).iconId as string | undefined,
          titlePrefix: enabled ? (
            <span
              className={styles.headerCheckbox}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                color="primary"
                onChange={(e) => setSelected([key], e.target.checked)}
              />
            </span>
          ) : undefined,
          selected: isSelected,
        })}
      >
        {milestone.steps
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((step) => renderStep(milestone, step))}
        <Button
          className={styles.addButton}
          startIcon={<AddIcon/>}
          onClick={() => {
            setEditingStep({ step: null, milestoneName: milestone.name });
            setIsStepDialogOpen(true);
          }}
        >
          Add Step
        </Button>
      </CustomAccordion>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress/>
          <Typography style={{ marginLeft: '16px' }}>Loading Configuration...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            <strong>Error:</strong>
            {' '}
            {error}
          </Typography>
          <Typography color="textSecondary">Please check the console and ensure the API server is running.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Title title="Milestones Configuration"/>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            gutterBottom
            component="h2"
            variant="h5"
          >
            Edit Milestones
          </Typography>
          {refreshing && <CircularProgress size={18}/>}
        </div>
        <NbaToolbar
          duplicateInProgress={refreshing}
          enabled={enabled}
          selectedCount={selectedIds.size}
          onClear={() => clearSelection()}
          onDuplicate={handleDuplicateSelected}
          onToggleEnabled={(next) => {
            if (!next) {
              reset();
              return;
            }
            setEnabled(true);
          }}
        />
        <div className={styles.editorRoot}>
          {
            milestones
              .slice()
              .sort((a, b) => ((a as any).order ?? 0) - ((b as any).order ?? 0))
              .map((milestone) => renderMilestone(milestone))
          }
          <Button
            className={styles.addButton}
            color="primary"
            startIcon={<AddIcon/>}
            variant="contained"
            onClick={() => {
              setEditingMilestone(null);
              setIsMilestoneDialogOpen(true);
            }}
          >
            Add Milestone
          </Button>
        </div>
      </CardContent>

      <EditMilestoneDialog
        milestone={editingMilestone}
        open={isMilestoneDialogOpen}
        onClose={() => setIsMilestoneDialogOpen(false)}
        onSave={handleSaveMilestone}
      />
      <EditStepDialog
        open={isStepDialogOpen}
        step={editingStep.step}
        onClose={() => setIsStepDialogOpen(false)}
        onSave={handleSaveStep}
      />
      <EditActionDialog
        action={editingAction.action}
        availableComponents={availableComponents}
        availableFunctions={availableFunctions}
        open={isActionDialogOpen}
        onClose={() => setIsActionDialogOpen(false)}
        onSave={handleSaveAction}
      />
      <DuplicateActionsDialog
        duplicateInProgress={refreshing}
        milestones={milestones}
        open={isDuplicateActionsDialogOpen}
        selectedCount={selectedIds.size}
        onClose={() => setIsDuplicateActionsDialogOpen(false)}
        onConfirm={handleConfirmDuplicateActions}
      />
    </Card>
  );
};
